import { Vector3 } from "three";

interface HorizontalPoint {
  station: number;
  easting: number;
  northing: number;
}

interface ProfilePoint {
  station: number;
  elevation: number;
}

/**
 * Parse a LandXML file and extract 3D centerline points.
 * Returns points centered around the origin for 3D rendering.
 */
export async function parseLandXML(
  xmlText: string
): Promise<{ points: Vector3[]; rawPoints: { station: number; easting: number; northing: number; elevation: number }[] }> {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, "application/xml");

  const hPoints = parseHorizontalAlignment(doc);
  const profile = parseProfile(doc);

  // Interpolate horizontal alignment at regular intervals
  const step = 2; // meters
  const totalLength = hPoints[hPoints.length - 1].station;
  const sampledH: HorizontalPoint[] = [];

  for (let s = 0; s <= totalLength; s += step) {
    sampledH.push(interpolateHorizontal(hPoints, s));
  }
  // Ensure we include the last point
  if (sampledH[sampledH.length - 1].station < totalLength) {
    sampledH.push(interpolateHorizontal(hPoints, totalLength));
  }

  // Combine horizontal with vertical profile
  const rawPoints = sampledH.map((h) => ({
    station: h.station,
    easting: h.easting,
    northing: h.northing,
    elevation: interpolateElevation(profile, h.station),
  }));

  // Center around origin for 3D scene
  const avgE = rawPoints.reduce((s, p) => s + p.easting, 0) / rawPoints.length;
  const avgN = rawPoints.reduce((s, p) => s + p.northing, 0) / rawPoints.length;
  const avgZ = rawPoints.reduce((s, p) => s + p.elevation, 0) / rawPoints.length;

  // Map to 3D: X = easting offset, Y = elevation offset, Z = northing offset
  const points = rawPoints.map(
    (p) => new Vector3(p.easting - avgE, p.elevation - avgZ, p.northing - avgN)
  );

  return { points, rawPoints };
}

function parseCoords(text: string): [number, number] {
  const parts = text.trim().split(/\s+/).map(Number);
  return [parts[0], parts[1]]; // easting, northing (LandXML Metric)
}

function parseHorizontalAlignment(doc: Document): HorizontalPoint[] {
  const ns = "http://www.landxml.org/schema/LandXML-1.2";
  const coordGeom = doc.getElementsByTagNameNS(ns, "CoordGeom")[0];
  if (!coordGeom) throw new Error("No CoordGeom found");

  const points: HorizontalPoint[] = [];
  let station = 0;

  for (const child of Array.from(coordGeom.children)) {
    const tag = child.localName;

    if (tag === "Line") {
      const length = parseFloat(child.getAttribute("length") || "0");
      const startEl = child.getElementsByTagNameNS(ns, "Start")[0];
      const endEl = child.getElementsByTagNameNS(ns, "End")[0];
      const [sE, sN] = parseCoords(startEl.textContent || "");
      const [eE, eN] = parseCoords(endEl.textContent || "");

      if (points.length === 0) {
        points.push({ station, easting: sE, northing: sN });
      }
      station += length;
      points.push({ station, easting: eE, northing: eN });
    } else if (tag === "Curve") {
      const length = parseFloat(child.getAttribute("length") || "0");
      const radius = parseFloat(child.getAttribute("radius") || "0");
      const rot = child.getAttribute("rot"); // "cw" or "ccw"
      const startEl = child.getElementsByTagNameNS(ns, "Start")[0];
      const centerEl = child.getElementsByTagNameNS(ns, "Center")[0];
      const endEl = child.getElementsByTagNameNS(ns, "End")[0];
      const [sE, sN] = parseCoords(startEl.textContent || "");
      const [cE, cN] = parseCoords(centerEl.textContent || "");
      const [eE, eN] = parseCoords(endEl.textContent || "");

      if (points.length === 0) {
        points.push({ station, easting: sE, northing: sN });
      }

      // Interpolate arc points
      const startAngle = Math.atan2(sN - cN, sE - cE);
      const endAngle = Math.atan2(eN - cN, eE - cE);
      const arcSteps = Math.max(Math.ceil(length / 2), 10);

      for (let i = 1; i <= arcSteps; i++) {
        const t = i / arcSteps;
        let angle: number;

        if (rot === "cw") {
          let sweep = startAngle - endAngle;
          if (sweep < 0) sweep += 2 * Math.PI;
          angle = startAngle - sweep * t;
        } else {
          let sweep = endAngle - startAngle;
          if (sweep < 0) sweep += 2 * Math.PI;
          angle = startAngle + sweep * t;
        }

        const pE = cE + radius * Math.cos(angle);
        const pN = cN + radius * Math.sin(angle);
        station += length / arcSteps;
        points.push({ station, easting: pE, northing: pN });
      }
    }
  }

  return points;
}

function parseProfile(doc: Document): ProfilePoint[] {
  const ns = "http://www.landxml.org/schema/LandXML-1.2";
  const profAlign = doc.getElementsByTagNameNS(ns, "ProfAlign")[0];
  if (!profAlign) throw new Error("No ProfAlign found");

  const points: ProfilePoint[] = [];

  for (const child of Array.from(profAlign.children)) {
    const tag = child.localName;
    const text = (child.textContent || "").trim();
    const parts = text.split(/\s+/).map(Number);

    if (tag === "PVI") {
      points.push({ station: parts[0], elevation: parts[1] });
    } else if (tag === "CircCurve") {
      // CircCurve text contains the PVI station and elevation
      points.push({ station: parts[0], elevation: parts[1] });
    }
  }

  return points;
}

function interpolateHorizontal(
  points: HorizontalPoint[],
  station: number
): HorizontalPoint {
  if (station <= points[0].station) return { ...points[0] };
  if (station >= points[points.length - 1].station)
    return { ...points[points.length - 1] };

  for (let i = 0; i < points.length - 1; i++) {
    if (station >= points[i].station && station <= points[i + 1].station) {
      const t =
        (station - points[i].station) /
        (points[i + 1].station - points[i].station);
      return {
        station,
        easting: points[i].easting + t * (points[i + 1].easting - points[i].easting),
        northing: points[i].northing + t * (points[i + 1].northing - points[i].northing),
      };
    }
  }
  return { ...points[points.length - 1] };
}

function interpolateElevation(
  profile: ProfilePoint[],
  station: number
): number {
  if (station <= profile[0].station) return profile[0].elevation;
  if (station >= profile[profile.length - 1].station)
    return profile[profile.length - 1].elevation;

  for (let i = 0; i < profile.length - 1; i++) {
    if (station >= profile[i].station && station <= profile[i + 1].station) {
      const t =
        (station - profile[i].station) /
        (profile[i + 1].station - profile[i].station);
      return profile[i].elevation + t * (profile[i + 1].elevation - profile[i].elevation);
    }
  }
  return profile[profile.length - 1].elevation;
}
